import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../AuthContext';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import Masonry from 'react-masonry-css';
import './PurchasedContent.css';

const PurchasedContent = () => {
    const { user } = useContext(AuthContext);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const breakpointColumnsObj = {
        default: 4,
        1100: 3,
        700: 2,
        500: 1
    };

    useEffect(() => {
        const fetchPurchasedContent = async () => {
            if (!user) return;

            try {
                const requestsRef = collection(db, 'custom_content_requests');
                const q = query(
                    requestsRef,
                    where('userId', '==', user.uid),
                    where('status', '==', 'fulfilled')
                );

                const querySnapshot = await getDocs(q);
                const purchasedItems = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })).filter(item => item.imageUrl); // Only include items with images

                setImages(purchasedItems);
            } catch (error) {
                console.error('Error fetching purchased content:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPurchasedContent();
    }, [user]);

    const handleImageClick = (image, index) => {
        setSelectedImage(image);
        setCurrentImageIndex(index);
        document.body.style.overflow = 'hidden';
    };

    const closeLightbox = () => {
        setSelectedImage(null);
        document.body.style.overflow = 'auto';
    };

    const showNextImage = (e) => {
        e.stopPropagation();
        const nextIndex = (currentImageIndex + 1) % images.length;
        setCurrentImageIndex(nextIndex);
        setSelectedImage(images[nextIndex]);
    };

    const showPrevImage = (e) => {
        e.stopPropagation();
        const prevIndex = (currentImageIndex - 1 + images.length) % images.length;
        setCurrentImageIndex(prevIndex);
        setSelectedImage(images[prevIndex]);
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!selectedImage) return;
            
            switch (event.keyCode) {
                case 27: // ESC
                    closeLightbox();
                    break;
                case 37: // Left arrow
                    showPrevImage(event);
                    break;
                case 39: // Right arrow
                    showNextImage(event);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedImage, currentImageIndex, images]);

    if (loading) {
        return (
            <div className="purchased-content">
                <h3>My Purchased Content</h3>
                <div className="loading-spinner"></div>
            </div>
        );
    }

    if (images.length === 0) {
        return (
            <div className="purchased-content">
                <h3>My Purchased Content</h3>
                <div className="empty-message">
                    <p>No purchased content yet.</p>
                    <p>Check out the Request Portal to request something special!</p>
                </div>
            </div>
        );
    }

    return (
        <div className="purchased-content">
            <h3>My Purchased Content</h3>
            
            <Masonry
                breakpointCols={breakpointColumnsObj}
                className="my-masonry-grid"
                columnClassName="my-masonry-grid_column"
            >
                {images.map((image, index) => (
                    <div 
                        key={image.id} 
                        className="masonry-item"
                        onClick={() => handleImageClick(image, index)}
                    >
                        <img 
                            src={image.imageUrl} 
                            alt={`Custom content ${index + 1}`}
                            loading="lazy"
                        />
                        <div className="image-overlay">
                            <div className="image-details">
                                <span>#{image.requestNumber}</span>
                                <span>{image.topOutfitLabel}</span>
                                <span>{image.outfitLabel}</span>
                                <span>{image.poseLabel}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </Masonry>

            {selectedImage && (
                <div className="lightbox" onClick={closeLightbox}>
                    <button className="lightbox-close" onClick={closeLightbox}>×</button>
                    
                    <button 
                        className="lightbox-nav-button prev"
                        onClick={showPrevImage}
                        aria-label="Previous image"
                    >
                        ‹
                    </button>
                    
                    <div className="lightbox-content" onClick={e => e.stopPropagation()}>
                        <div className="image-container">
                            <img 
                                src={selectedImage.imageUrl} 
                                alt={`Custom content ${currentImageIndex + 1}`}
                            />
                        </div>
                        
                        <div className="lightbox-details">
                            <div className="details-text">
                                <span>#{selectedImage.requestNumber}</span>
                                <span className="outfit-details">
                                    {selectedImage.topOutfitLabel} • {selectedImage.outfitLabel} • {selectedImage.poseLabel}
                                </span>
                            </div>
                            <span className="image-counter">
                                {currentImageIndex + 1} / {images.length}
                            </span>
                        </div>
                    </div>

                    <button 
                        className="lightbox-nav-button next"
                        onClick={showNextImage}
                        aria-label="Next image"
                    >
                        ›
                    </button>
                </div>
            )}
        </div>
    );
};

export default PurchasedContent;

